import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router';
import {
  Container,
  Typography,
  Box,
  Chip,
  Paper,
  Divider,
  CircularProgress,
  IconButton,
  Card,
  CardContent,
  Avatar,
  TextField,
  Button,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faPaperPlane } from '@fortawesome/pro-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { MarkdownRenderer } from '../../Components/MarkdownRenderer';
import {
  MDXEditor,
  UndoRedo,
  BoldItalicUnderlineToggles,
  toolbarPlugin,
  listsPlugin,
  ListsToggle,
  BlockTypeSelect,
  headingsPlugin,
  StrikeThroughSupSubToggles,
} from '@mdxeditor/editor';
import '@mdxeditor/editor/style.css';
import { YouTrackIssueDetailDTO, YouTrackCommentDTO, CommentAuthorType } from '../../apiClient/Api';
import { useApplicationContext } from 'ApplicationContext';

/**
 * TicketDetails component displays detailed information about a specific YouTrack ticket.
 */
const TicketDetails: React.FC = () => {
  const { t } = useTranslation();
  const context = useApplicationContext();
  const navigate = useNavigate();
  const { ticketId } = useParams<{ ticketId: string }>();
  const [searchParams] = useSearchParams();
  const [ticket, setTicket] = useState<YouTrackIssueDetailDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [isReporter, setIsReporter] = useState(false);
  const [checkingReporter, setCheckingReporter] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);

  // Get filterEmail from URL query parameters
  const filterEmail = searchParams.get('filterEmail');

  /**
   * Navigates back to the tickets list, preserving the filterEmail parameter if present.
   */
  const navigateBackToTickets = () => {
    navigate(filterEmail ? `/tickets?filterEmail=${encodeURIComponent(filterEmail)}` : '/tickets');
  };

  useEffect(() => {
    if (ticketId) {
      context.setTitle(`${t('ticketDetails.title')} - ${ticketId}`);
      fetchTicketDetails();
      checkIsReporter();
    }
  }, [ticketId, filterEmail]);

  /**
   * Fetches ticket details from YouTrack API.
   */
  const fetchTicketDetails = async () => {
    if (!ticketId) return;

    try {
      setLoading(true);
      setAccessDenied(false);
      const response = await context.api.youTrack.youTrackGetTicketDetails(ticketId, {
        filterEmail: filterEmail || undefined
      });
      setTicket(response.data);
    } catch (error: any) {
      console.error("Failed to fetch ticket details:", error);
      
      // Check if it's a 403 Forbidden error
      if (error?.response?.status === 403) {
        setAccessDenied(true);
      } else {
        context.apiError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Checks if the current user is the reporter of the ticket.
   */
  const checkIsReporter = async () => {
    if (!ticketId) return;

    try {
      setCheckingReporter(true);
      const response = await context.api.youTrack.youTrackIsReporter(ticketId, {
        filterEmail: filterEmail || undefined
      });
      setIsReporter(response.data);
    } catch (error) {
      console.error("Failed to check reporter status:", error);
      setIsReporter(false);
    } finally {
      setCheckingReporter(false);
    }
  };

  /**
   * Submits a new comment to the ticket.
   */
  const handleSubmitComment = async () => {
    if (!ticketId || !commentText.trim()) return;

    try {
      setSubmitting(true);
      await context.api.youTrack.youTrackCreateComment(ticketId, {
        text: commentText,
        isPublic: true,
      }, {
        filterEmail: filterEmail || undefined
      });
      setCommentText('');
      // Reload ticket details to show the new comment
      await fetchTicketDetails();
    } catch (error) {
      console.error("Failed to submit comment:", error);
      context.apiError(error);
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Gets the color for status chip based on status text.
   */
  const getStatusColor = (
    status: string | null | undefined
  ): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    if (!status) return 'default';

    const statusLower = status.toLowerCase();
    if (statusLower.includes('open') || statusLower.includes('offen')) return 'info';
    if (statusLower.includes('progress') || statusLower.includes('bearbeitung')) return 'warning';
    if (statusLower.includes('done') || statusLower.includes('erledigt') || statusLower.includes('closed'))
      return 'success';
    if (statusLower.includes('rejected') || statusLower.includes('abgelehnt')) return 'error';

    return 'default';
  };

  /**
   * Formats timestamp to readable date string.
   */
  const formatDate = (timestamp: number | null | undefined): string => {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleString();
  };

  /**
   * Checks if a string contains HTML tags.
   */
  const isHtml = (str: string | null | undefined): boolean => {
    if (!str) return false;
    return /<\/?[a-z][\s\S]*>/i.test(str);
  };

  /**
   * Renders a single comment.
   */
  const renderComment = (comment: YouTrackCommentDTO, index: number) => {
    if (comment.isDeleted) return null;

    const authorType = comment.authorType;
    const isMine = authorType === CommentAuthorType.Mine;
    const isAgent = authorType === CommentAuthorType.Agent;
    const isOther = authorType === CommentAuthorType.Other;

    // Bestimme Farben und Labels basierend auf authorType
    let borderColor = 'transparent';
    let bgColor = 'background.paper';
    let avatarColor = 'grey.500';
    let badgeLabel = '';
    let badgeColor: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' = 'default';

    if (isAgent) {
      borderColor = 'success.main';
      bgColor = 'success.50';
      avatarColor = 'success.main';
      badgeLabel = t('ticketDetails.agent');
      badgeColor = 'success';
    } else if (isMine) {
      borderColor = 'primary.main';
      bgColor = 'primary.50';
      avatarColor = 'primary.main';
      badgeLabel = t('ticketDetails.mine');
      badgeColor = 'primary';
    } else if (isOther) {
      borderColor = 'info.main';
      bgColor = 'info.50';
      avatarColor = 'info.main';
      badgeLabel = t('ticketDetails.other');
      badgeColor = 'info';
    }

    return (
      <Card 
        key={index} 
        sx={{ 
          mb: 2,
          borderLeft: borderColor !== 'transparent' ? '4px solid' : 'none',
          borderLeftColor: borderColor,
          bgcolor: bgColor,
        }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Avatar sx={{ 
              mr: 2, 
              bgcolor: avatarColor,
              fontWeight: 'bold',
            }}>
              {comment.author?.displayName?.charAt(0) || '?'}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  {comment.author?.displayName || comment.author?.login || t('ticketDetails.unknownAuthor')}
                </Typography>
                {badgeLabel && (
                  <Chip 
                    label={badgeLabel} 
                    size="small" 
                    color={badgeColor}
                    sx={{ height: '20px', fontSize: '0.7rem' }}
                  />
                )}
              </Box>
              <Typography variant="caption" color="text.secondary">
                {formatDate(comment.created)}
              </Typography>
            </Box>
          </Box>
          <Divider sx={{ my: 1 }} />
          {comment.text ? (
            isHtml(comment.text) ? (
              <Box
                sx={{
                  '& *': {
                    maxWidth: '100%',
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                  },
                  '& img': {
                    maxWidth: '100%',
                    height: 'auto',
                  },
                }}
                dangerouslySetInnerHTML={{ __html: comment.text }}
              />
            ) : (
              <Box sx={{ '& p:first-of-type': { marginTop: 0 }, '& p:last-of-type': { marginBottom: 0 }, '& p': { whiteSpace: 'pre-line' } }}>
                <MarkdownRenderer>{comment.text}</MarkdownRenderer>
              </Box>
            )
          ) : (
            <Typography variant="body2">-</Typography>
          )}
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (accessDenied) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={navigateBackToTickets} color="primary">
            <FontAwesomeIcon icon={faArrowLeft} />
          </IconButton>
          <Typography variant="h4">{t('ticketDetails.title')}</Typography>
        </Box>
        <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'error.50' }}>
          <Typography variant="h5" color="error" gutterBottom>
            {t('ticketDetails.accessDenied')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
            {t('ticketDetails.accessDeniedMessage')}
          </Typography>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={navigateBackToTickets}
            sx={{ mt: 3 }}
          >
            {t('ticketDetails.backToTickets')}
          </Button>
        </Paper>
      </Container>
    );
  }

  if (!ticket) {
    return (
      <Container maxWidth="lg">
        <Typography variant="h5" color="error">
          {t('ticketDetails.notFound')}
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={navigateBackToTickets} color="primary">
          <FontAwesomeIcon icon={faArrowLeft} />
        </IconButton>
        <Typography variant="h4">{t('ticketDetails.title')}</Typography>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h5"
              gutterBottom
              sx={{ fontFamily: 'monospace', fontWeight: 'bold', color: 'primary.main', lineHeight: 'inherit' }}
            >
              {ticket.idReadable}
            </Typography>
            <Typography variant="h6" gutterBottom>
              {ticket.summary}
            </Typography>
            <Box sx={{ display: 'flex', gap: 3, mt: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {t('ticketDetails.created')}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                  {formatDate(ticket.created)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {t('ticketDetails.updated')}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                  {formatDate(ticket.updated)}
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
            <Chip label={ticket.status || '-'} color={getStatusColor(ticket.status)} />
            {ticket.reporter && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('ticketDetails.reportedBy')}:
                </Typography>
                <Avatar
                  sx={{
                    width: 24,
                    height: 24,
                    fontSize: '0.75rem',
                    bgcolor: 'info.main',
                  }}
                >
                  {ticket.reporter.displayName?.charAt(0) || ticket.reporter.login?.charAt(0) || '?'}
                </Avatar>
                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                  {ticket.reporter.displayName || ticket.reporter.login || t('ticketDetails.unknownAuthor')}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
            {t('ticketDetails.description')}
          </Typography>
          {ticket.description ? (
            isHtml(ticket.description) ? (
              <Box
                sx={{
                  color: 'text.secondary',
                  '& *': {
                    maxWidth: '100%',
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                  },
                  '& img': {
                    maxWidth: '100%',
                    height: 'auto',
                  },
                }}
                dangerouslySetInnerHTML={{ __html: ticket.description }}
              />
            ) : (
              <Box sx={{ color: 'text.secondary', '& p:first-of-type': { marginTop: 0 }, '& p:last-of-type': { marginBottom: 0 }, '& p': { whiteSpace: 'pre-line' } }}>
                <MarkdownRenderer>{ticket.description}</MarkdownRenderer>
              </Box>
            )
          ) : (
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', color: 'text.secondary' }}>
              {t('ticketDetails.noDescription')}
            </Typography>
          )}
        </Box>
      </Paper>

      {ticket.comments && ticket.comments.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
            {t('ticketDetails.comments')} ({ticket.comments.length})
          </Typography>
          {ticket.comments.map((comment, index) => renderComment(comment, index))}
        </Box>
      )}

      {(!ticket.comments || ticket.comments.length === 0) && (
        <Paper sx={{ p: 3, textAlign: 'center', mb: 3 }}>
          <Typography variant="body2" color="text.secondary">
            {t('ticketDetails.noComments')}
          </Typography>
        </Paper>
      )}

      {!checkingReporter && (
        isReporter ? (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('ticketDetails.addComment')}
            </Typography>
            <Box sx={{ 
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              mb: 2,
              '& .mdxeditor': {
                backgroundColor: 'background.paper',
              }
            }}>
              <MDXEditor
                markdown={commentText}
                onChange={setCommentText}
                plugins={[
                  listsPlugin(),
                  headingsPlugin(),
                  toolbarPlugin({
                    toolbarContents: () => (
                      <>
                        <UndoRedo />
                        <BoldItalicUnderlineToggles />
                        <StrikeThroughSupSubToggles />
                        <ListsToggle />
                      </>
                    )
                  })
                ]}
              />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              {t('ticketDetails.markdownSupported')}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<FontAwesomeIcon icon={faPaperPlane} />}
                onClick={handleSubmitComment}
                disabled={submitting || !commentText.trim()}
              >
                {submitting ? t('ticketDetails.submitting') : t('ticketDetails.submit')}
              </Button>
            </Box>
          </Paper>
        ) : (
          <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'warning.50' }}>
            <Typography variant="body1" color="text.secondary">
              {t('ticketDetails.notReporterMessage')}
            </Typography>
          </Paper>
        )
      )}
    </Container>
  );
};

export default TicketDetails;
